import { type Address, getAddress } from 'viem'

import type { OracleDecoding } from '@/lib/procedures/oracleDecoding.contract'
import type { OracleMethod } from '@/lib/upstream/bridge/routes/oracle-decoding'

type Fact = OracleDecoding['facts'][number]

export const EMPTY = '—'

export type Side = 'collateral' | 'loan'

export const SIDE_LABEL: Record<Side, string> = { collateral: 'Collateral', loan: 'Loan' }

export const FEED_TYPE_LABEL = {
  market: 'Market price',
  fundamental: 'Fundamental',
  nav: 'NAV',
  dex: 'DEX price',
  formula: 'Formula',
  mixed: 'Mixed',
  fixed: 'Fixed',
  fixedOne: 'Fixed 1:1'
} satisfies Record<Exclude<OracleMethod, 'unknown'> | 'fixed' | 'fixedOne', string>

/** Whether a row has provider, admin or update facts to expand; an unset leg has none. */
export const hasFeedDetails = (row: FeedRow): boolean =>
  row.address !== null ||
  row.proof !== null ||
  row.provider !== null ||
  row.admins !== null ||
  row.heartbeat !== null ||
  row.deviation !== null ||
  row.age !== null ||
  row.children.length > 0

const CONSTANT_FEED_SHAPE = 'constant-value-feed'

/** Wrapper nodes, not data sources: the oracle contracts themselves. */
const HIDDEN_PROVIDERS = new Set(['morpho'])

const PROVIDER_NAME: Record<string, string> = {
  api3: 'API3',
  chainlink: 'Chainlink',
  chronicle: 'Chronicle',
  eoracle: 'eOracle',
  lido: 'Lido',
  ojo: 'Ojo',
  pendle: 'Pendle',
  pyth: 'Pyth',
  redstone: 'RedStone',
  spark: 'Spark',
  stork: 'Stork'
}

/** Provider sites used when the API has no provider metadata. */
export const providerLinks: Record<string, string> = {
  chainlink: 'https://data.chain.link',
  chronicle: 'https://chroniclelabs.org',
  pendle: 'https://docs.pendle.finance',
  redstone: 'https://redstone.finance',
  pyth: 'https://pyth.network',
  ojo: 'https://ojo.network',
  lido: 'https://lido.fi',
  spark: 'https://spark.fi'
}

export interface Provider {
  id: string
  name: string
  logo: string | null
  site: string | null
}

export interface FeedRow {
  id: string
  /** `{base} / {quote}` from the API, else the declared pair or the decoder's shape name. */
  name: string
  /** The declared pair or shape name when `name` comes from the API's base / quote, else `null`. */
  listedAs: string | null
  /** The feed's contract; `null` for a fixed side, which has none. */
  address: Address | null
  /** Label of the feed's `method`, `—` when the API doesn't know it. */
  type: string
  providerType: { label: string; providerName: string } | null
  feedUrl: string | null
  provider: Provider | null
  /** `null` when the API doesn't know who can change the feed. */
  admins: Address[] | null
  heartbeat: number | null
  heartbeatAbsence: 'none' | 'unknown' | null
  deviation: number | null
  price: number | null
  /** Seconds between the feed's last update and the API's read; `null` when it reports no age. */
  age: number | null
  terms: { maturity: number | null; baseDiscountPerYear: string | null } | null
  /** Where to verify a leg with no feed reads as 1; `null` on a row backed by a feed contract. */
  proof: LegProof | null
  /** Base and quote units parsed from the feed's declared pair; `null` when it declares none. */
  pair: FeedPair | null
  /** The feeds directly under this one in the oracle's tree, one level only. */
  children: FeedRow[]
}

/** The provider or decoder label to show on a feed type badge. */
export function typeBadgeLabel(row: Pick<FeedRow, 'type' | 'providerType'>): string | null {
  return row.providerType?.label ?? (row.type === EMPTY ? null : row.type)
}

export interface FeedPair {
  base: string
  quote: string
}

/** `"A / B"`, or a description ending in one such as `"Custom price feed for wstETH / ETH"`. */
export function parsePair(declared: string | null | undefined): FeedPair | null {
  const match = declared?.match(/(\S+)\s*\/\s*(\S+)/)
  return match?.[1] && match[2] ? { base: match[1], quote: match[2] } : null
}

const ORACLE_LIBRARIES =
  'https://github.com/morpho-org/morpho-blue-oracles/blob/9d2a0b66589d0669c52e47310af8eb804facf97e/src/morpho-chainlink/libraries'

/** The library line that returns 1 for an empty slot. */
export const LEG_CODE = {
  feed: {
    label: 'ChainlinkDataFeedLib.sol L21',
    url: `${ORACLE_LIBRARIES}/ChainlinkDataFeedLib.sol#L21`
  },
  vault: { label: 'VaultLib.sol L14', url: `${ORACLE_LIBRARIES}/VaultLib.sol#L14` }
} as const

export interface LegProof {
  /** The oracle contract whose empty slot gives the 1. */
  oracle: Address
  code: (typeof LEG_CODE)[keyof typeof LEG_CODE]
}

export interface FeedSection {
  side: Side
  rows: FeedRow[]
}

export interface FeedBranch {
  id: string
  /** `Feed 1` / `Feed 2` on a two-feed oracle, `null` on a single one. */
  title: string | null
  sections: [FeedSection, FeedSection]
  /** Feed slots left empty on each side, which the oracle reads as 1 (`base feed 2`, `quote vault`). */
  unset: Record<Side, string[]>
  /** The oracle contract this branch reads, `null` when the decoding doesn't include it. */
  oracle: Address | null
  /** Nodes outside any feed that have their own admin, such as the router on a two-feed oracle. */
  oracleRows: FeedRow[]
}

export interface OracleAdminSummary {
  known: Address[]
  unknown: string[]
}

export interface OracleFeeds {
  branches: FeedBranch[]
  /** The providers of the rows the table shows, each once, for the oracle summary. */
  providers: Provider[]
  /** Known addresses and the visible feed labels whose admins could not be read. */
  admins: OracleAdminSummary
  /** When the feed values were read, in seconds since the epoch; the oracle price is read live. */
  observedAt: number
}

const BRANCHES = [
  { segment: 'primary oracle', id: 'primary', title: 'Feed 1' },
  { segment: 'backup oracle', id: 'backup', title: 'Feed 2' }
] as const

const numberOf = (signal: Fact['price']) => {
  if (!signal) return null
  const value = Number(signal.value)
  return Number.isFinite(value) ? value : null
}

const sideOf = (segment: string): Side | null => {
  if (segment.startsWith('base')) return 'collateral'
  if (segment.startsWith('quote')) return 'loan'
  return null
}

const isPrefix = (prefix: readonly string[], path: readonly string[]) =>
  prefix.length < path.length && prefix.every((segment, index) => path[index] === segment)

/** Whether a decoded node has multiple direct oracle children. */
export function hasMultipleOracles(decoding: OracleDecoding): boolean {
  const childOraclesByParent = new Map<string, Set<string>>()
  for (const { path } of decoding.facts) {
    const edge = path.at(-1)
    if (!edge?.endsWith(' oracle')) continue
    const parentPath = JSON.stringify(path.slice(0, -1))
    const childOracles = childOraclesByParent.get(parentPath) ?? new Set<string>()
    childOracles.add(edge)
    childOraclesByParent.set(parentPath, childOracles)
  }
  return decoding.facts.some(
    ({ path }) => (childOraclesByParent.get(JSON.stringify(path))?.size ?? 0) >= 2
  )
}

const comparePaths = (a: Fact, b: Fact) =>
  a.path.join('/').localeCompare(b.path.join('/'), 'en', { numeric: true })

const reportsValue = (fact: Fact) =>
  fact.price !== null || fact.heartbeat !== null || fact.deviation !== null

/** Where a feed's provider name links: its feed page, the provider site, or the static fallback. */
export const getProviderUrl = (feed: Pick<FeedRow, 'feedUrl' | 'provider'>) =>
  feed.feedUrl ?? feed.provider?.site ?? (feed.provider && providerLinks[feed.provider.id]) ?? null

type Providers = OracleDecoding['providers']

const providerOf = (id: string | null, providers: Providers): Provider | null => {
  if (!id || HIDDEN_PROVIDERS.has(id)) return null
  const metadata = providers[id]
  return {
    id,
    name: metadata?.name ?? PROVIDER_NAME[id] ?? id.charAt(0).toUpperCase() + id.slice(1),
    logo: metadata?.logo ?? null,
    site: metadata?.site ?? null
  }
}

/** A constant feed that returns 1 is a fixed 1:1 leg, like a side with no feed. */
const constantType = (price: Fact['price'] | undefined) =>
  price != null && Number(price.value) === 1 ? 'fixedOne' : 'fixed'

export const feedTypeLabel = (
  fact: Pick<Fact, 'method' | 'shape'> & Partial<Pick<Fact, 'price'>>
) => {
  const key = fact.shape?.id === CONSTANT_FEED_SHAPE ? constantType(fact.price) : fact.method
  return key === 'unknown' ? EMPTY : FEED_TYPE_LABEL[key]
}

function toRow(fact: Fact, providers: Providers): FeedRow {
  const fixed = fact.shape?.id === CONSTANT_FEED_SHAPE
  const age = fixed ? null : fact.age
  const heartbeat = numberOf(fact.heartbeat)
  const listedAs = fact.declaredPair ?? fact.shape?.displayName ?? EMPTY
  const unitPair = fact.base && fact.quote ? `${fact.base} / ${fact.quote}` : null
  const providerMetadata = providers[fact.providerType?.directory ?? '']
  return {
    id: fact.path.join('/'),
    name: unitPair ?? listedAs,
    // TODO(cash): some nodes have no `base`/`quote` (e.g. Monad RedStone feeds); symbols come as the API cases them (e.g. STETH).
    listedAs: unitPair === null ? null : listedAs,
    address: getAddress(fact.address),
    type: feedTypeLabel(fact),
    providerType: fact.providerType
      ? {
          label: fact.providerType.label,
          providerName:
            providerMetadata?.name ??
            PROVIDER_NAME[fact.providerType.directory] ??
            fact.providerType.directory
        }
      : null,
    feedUrl: fact.feedUrl,
    provider: providerOf(fact.provider, providers),
    admins: fact.changeableBy && fact.changeableBy.map(address => getAddress(address)),
    heartbeat,
    heartbeatAbsence: fact.heartbeatAbsence,
    deviation: numberOf(fact.deviation),
    price: numberOf(fact.price),
    age,
    terms: fact.terms,
    proof: null,
    pair: parsePair(fact.declaredPair),
    children: []
  }
}

/**
 * The first nodes under `node`, itself included, that report a value. Wrappers with no value
 * (a vault adapter, a Chainlink aggregator under its proxy, a PT token) stay hidden.
 */
function valueRows(node: Fact, facts: readonly Fact[]): Fact[] {
  if (reportsValue(node)) return [node]
  return facts
    .filter(fact => fact.path.length === node.path.length + 1 && isPrefix(node.path, fact.path))
    .flatMap(child => valueRows(child, facts))
}

/** A leg's rows; a leg that reports nothing anywhere shows as one row for the leg itself. */
function legRows(leg: Fact, facts: readonly Fact[]): Fact[] {
  const rows = valueRows(leg, facts)
  return rows.length ? rows : [leg]
}

/**
 * A feed's row and the feeds directly under it, one level deep. A direct child that reports nothing
 * (an adapter or wrapper) is shown as the feeds under it that do, so no child row is blank.
 */
function withChildren(
  fact: Fact,
  facts: readonly Fact[],
  toFeedRow: (fact: Fact) => FeedRow
): FeedRow {
  const children = facts
    .filter(child => child.path.length === fact.path.length + 1 && isPrefix(fact.path, child.path))
    .flatMap(child => valueRows(child, facts))
    .map(toFeedRow)
  return { ...toFeedRow(fact), children }
}

function toBranch(
  prefix: readonly string[],
  facts: readonly Fact[],
  toFeedRow: (fact: Fact) => FeedRow
): [FeedSection, FeedSection] | null {
  const legs = facts.filter(
    fact => fact.path.length === prefix.length + 1 && isPrefix(prefix, fact.path)
  )
  const sections: [FeedSection, FeedSection] = [
    { side: 'collateral', rows: [] },
    { side: 'loan', rows: [] }
  ]
  for (const leg of legs) {
    const side = sideOf(leg.path[prefix.length] ?? '')
    if (!side) return null
    const section = side === 'collateral' ? sections[0] : sections[1]
    section.rows.push(...legRows(leg, facts).map(fact => withChildren(fact, facts, toFeedRow)))
  }
  return sections
}

const ROUTED_ORACLE = 'routed oracle'
const ROUTER_SHAPE = 'oracle-router'

/**
 * Whether a node the table hides has an admin the row above it (`covering`) doesn't show: an
 * unknown one, or an address it doesn't list. `undefined` when no row is above it.
 */
function addsAdmin(fact: Fact, covering: Address[] | null | undefined): boolean {
  const admins = fact.changeableBy?.map(address => getAddress(address)) ?? null
  if (covering === undefined) return admins === null || admins.length > 0
  if (admins === null) return covering !== null
  return admins.some(address => !(covering ?? []).includes(address))
}

/** A node with no value of its own, shown for its admin: no type, the router named as such. */
function toAdminRow(fact: Fact, providers: Providers): FeedRow {
  const row = { ...toRow(fact, providers), type: EMPTY, providerType: null }
  return fact.shape?.id === ROUTER_SHAPE ? { ...row, name: 'Oracle router', listedAs: null } : row
}

/**
 * Gives every hidden node with its own admin a row, so the table shows each admin the summary
 * does: under the nearest row above it, or as an oracle row when no row is above it.
 */
function addAdminRows(
  branches: FeedBranch[],
  prefixes: (readonly string[])[],
  facts: readonly Fact[],
  providers: Providers
) {
  const shown = new Map<string, { row: FeedRow; top: FeedRow }>()
  for (const top of branches.flatMap(branch => branch.sections.flatMap(section => section.rows))) {
    shown.set(top.id, { row: top, top })
    for (const child of top.children) shown.set(child.id, { row: child, top })
  }
  const byDepth = [...facts].sort((a, b) => a.path.length - b.path.length || comparePaths(a, b))
  const touched = new Set<FeedRow>()
  for (const fact of byDepth) {
    const id = fact.path.join('/')
    if (shown.has(id)) continue
    const ancestor = fact.path
      .map((_, index) => shown.get(fact.path.slice(0, fact.path.length - index - 1).join('/')))
      .find(entry => entry !== undefined && entry.row.id !== '')
    const ancestorPath = ancestor?.row.id.split('/')
    if (ancestor && ancestorPath && isPrefix(ancestorPath, fact.path)) {
      if (!addsAdmin(fact, ancestor.row.admins)) continue
      const row = toAdminRow(fact, providers)
      ancestor.top.children.push(row)
      touched.add(ancestor.top)
      shown.set(id, { row, top: ancestor.top })
      continue
    }
    if (!addsAdmin(fact, undefined)) continue
    const index = prefixes.findIndex(
      prefix =>
        (prefix.length === fact.path.length &&
          prefix.every((segment, i) => segment === fact.path[i])) ||
        isPrefix(prefix, fact.path) ||
        isPrefix(fact.path, prefix)
    )
    const branch = branches[index === -1 ? 0 : index]
    if (!branch) continue
    const row = toAdminRow(fact, providers)
    branch.oracleRows.push(row)
    shown.set(id, { row, top: row })
  }
  for (const top of touched)
    top.children.sort((a, b) => a.id.localeCompare(b.id, 'en', { numeric: true }))
}

/** A router forwards to one oracle; its feeds hang off that oracle, so read the branch from there. */
function throughRouters(prefix: readonly string[], facts: readonly Fact[]): readonly string[] {
  const routed = [...prefix, ROUTED_ORACLE]
  return facts.some(fact => fact.path.join('/') === routed.join('/'))
    ? throughRouters(routed, facts)
    : prefix
}

const UNSET_SLOT = /^(.+) is unset/

/** The decoder's `assumed_leg` findings on the oracle node at `prefix`, split by side. */
function unsetSlots(decoding: OracleDecoding, prefix: readonly string[]): Record<Side, string[]> {
  const unset: Record<Side, string[]> = { collateral: [], loan: [] }
  for (const finding of decoding.checks.assumed_leg?.findings ?? []) {
    if (finding.path.join('/') !== prefix.join('/')) continue
    const slot = UNSET_SLOT.exec(finding.detail)?.[1]
    const side = slot && sideOf(slot)
    if (side) unset[side].push(slot)
  }
  return unset
}

const andDescendants = (row: FeedRow): FeedRow[] => [row, ...row.children.flatMap(andDescendants)]
const unique = <T>(values: readonly T[], key: (value: T) => string) => [
  ...new Map(values.map(value => [key(value), value])).values()
]

/**
 * Flat feed rows for the market page, grouped Collateral / Loan (and Feed 1 / Feed 2 on a
 * backup oracle). `null` when the decoding is incomplete or its shape isn't one this view reads.
 */
export function toOracleFeeds(decoding: OracleDecoding): OracleFeeds | null {
  if (!decoding.coverage.complete) return null
  const facts = [...decoding.facts].sort(comparePaths)
  const root = facts.find(fact => fact.path.length === 0)
  const toFeedRow = (fact: Fact) => toRow(fact, decoding.providers)

  const wrappers = BRANCHES.filter(branch =>
    facts.some(fact => fact.path.length === 1 && fact.path[0] === branch.segment)
  )
  const branches: FeedBranch[] = []
  const prefixes: (readonly string[])[] = []
  const nodeAt = (path: readonly string[]) =>
    path.length === 0 ? root : facts.find(fact => fact.path.join('/') === path.join('/'))
  const targets =
    wrappers.length === 0
      ? [{ id: 'main', title: null, segments: [] as string[] }]
      : wrappers.map(wrapper => ({ ...wrapper, segments: [wrapper.segment] }))
  for (const target of targets) {
    const prefix = throughRouters(target.segments, facts)
    const sections = toBranch(prefix, facts, toFeedRow)
    if (!sections) return null
    const node = nodeAt(prefix)
    branches.push({
      id: target.id,
      title: target.title,
      sections,
      unset: unsetSlots(decoding, prefix),
      oracle: node ? getAddress(node.address) : null,
      oracleRows: []
    })
    prefixes.push(prefix)
  }
  addAdminRows(branches, prefixes, facts, decoding.providers)

  const rows = branches.flatMap(branch => [
    ...branch.oracleRows,
    ...branch.sections.flatMap(section => section.rows)
  ])
  if (rows.length === 0) return null
  const visible = rows.flatMap(andDescendants)

  return {
    branches,
    providers: credit(rows).providers,
    admins: {
      known: unique(
        visible.flatMap(row => row.admins ?? []),
        address => address
      ),
      unknown: visible.flatMap(row => (row.admins === null ? [row.name] : []))
    },
    observedAt: decoding.observedAt
  }
}

/** Who provided a table's feeds, each once. */
export function credit(rows: FeedRow[]): { providers: Provider[] } {
  return {
    providers: unique(
      rows.flatMap(andDescendants).flatMap(row => (row.provider ? [row.provider] : [])),
      provider => provider.id
    )
  }
}

const integer = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 })
const twoDecimals = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})
const sixSignificant = new Intl.NumberFormat('en-US', {
  maximumSignificantDigits: 6,
  maximumFractionDigits: 20
})

/** ≥ 1,000: 2 decimals (`80,602.55`). Below: up to 6 significant digits, no trailing zeros (`0.000405123`). */
export function formatFeedValue(value: number | null) {
  if (value === null) return EMPTY
  return Math.abs(value) >= 1_000 ? twoDecimals.format(value) : sixSignificant.format(value)
}

const UNITS = [
  ['d', 86_400],
  ['h', 3_600],
  ['m', 60]
] as const

/** `20m`, `24h`, `1d`; a heartbeat that isn't a whole number of a unit falls back to smaller ones. */
export function formatDuration(seconds: number) {
  for (const [unit, size] of UNITS) {
    if (seconds >= size && seconds % size === 0) return `${seconds / size}${unit}`
  }
  if (seconds >= 60) return `${integer.format(seconds / 60)}m`
  return `${seconds}s`
}

/** Elapsed time rounded down to its largest unit, as in `Last price update: 15m ago`. */
export function formatAge(seconds: number) {
  for (const [unit, size] of UNITS) {
    if (seconds >= size) return `${Math.floor(seconds / size)}${unit}`
  }
  return '<1m'
}

const dayAndClock = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'UTC'
})

/** `Oct 6, 14:10 UTC`. */
export const formatUtcTime = (seconds: number) => `${dayAndClock.format(seconds * 1000)} UTC`

/** Stale marks and ages come from the API's own read, so polling only picks up a new snapshot soon after the bridge publishes one. */
export const DECODING_REFRESH_MS = 60_000

export const formatDeviation = (deviation: number) => `${integer.format(deviation * 100)}%`

/** The heartbeat value, `None` when the API says there is no heartbeat, or `null` when unknown. */
export function formatHeartbeat({
  heartbeat,
  heartbeatAbsence
}: Pick<FeedRow, 'heartbeat' | 'heartbeatAbsence'>): string | null {
  if (heartbeat !== null) return formatDuration(heartbeat)
  return heartbeatAbsence === 'none' ? 'None' : null
}

export function formatUpdates({
  heartbeat,
  heartbeatAbsence,
  deviation
}: Pick<FeedRow, 'heartbeat' | 'heartbeatAbsence' | 'deviation'>) {
  const heartbeatLabel = formatHeartbeat({ heartbeat, heartbeatAbsence }) ?? EMPTY
  if (heartbeatLabel === EMPTY && deviation === null) return EMPTY
  return `${heartbeatLabel} / ${deviation === null ? EMPTY : formatDeviation(deviation)}`
}

/** A feed is stale once its age passes this many heartbeats. An assumption, kept here to tune. */
export const STALE_AFTER_HEARTBEATS = 1

/**
 * Judged from the API's `age` at its read, since a feed may have updated after it. `false` without an
 * age or a heartbeat: staleness can't be told.
 */
export function isStale({ age, heartbeat }: Pick<FeedRow, 'age' | 'heartbeat'>) {
  return age !== null && heartbeat !== null && age > heartbeat * STALE_AFTER_HEARTBEATS
}
